import * as React from 'react'

import useData from 'modules/core/components/DataTable/hooks/useData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

interface Props {
  children: React.ReactNode
}

interface Context {
  selectedRows: Set<string>
  onSelectRows: (ids: string[]) => void
  onUnselectRows: (ids: string[]) => void
}

export const Context = React.createContext<Context | null>(null)

const SelectedRowsProvider = <T, S extends string>({ children }: Props) => {
  const { data } = useData<T, S>()
  const [selectedRows, setSelectedRows] = React.useState<Set<string>>(new Set())

  const onSelectRows = (ids: string[]) => {
    const newSet = new Set(selectedRows)
    ids.forEach((id) => {
      const found = data.find((data) => {
        // NOTE: We don't like this kind of type assertions, but otherwise we should be extending T
        // as object everywhere. Kind of killer!
        return String((data as { [id: string]: string | number }).id) === String(id)
      })

      if (found) {
        newSet.add(String(id))
      }
    })
    setSelectedRows(newSet)
  }

  const onUnselectRows = (ids: string[]) => {
    const newSet = new Set(selectedRows)
    // NOTE: We could also throw if the row does not exist
    ids.map((id) => newSet.has(id) && newSet.delete(id))
    setSelectedRows(newSet)
  }

  // If data changes, we need to unselect the selected rows if they are not present anymore on the
  // table!
  useUnsafeEffect(() => {
    if (selectedRows.size === 0) return

    const newSet = new Set(selectedRows)

    data.forEach((el) => {
      const id = (el as { [id: string]: string | number }).id
      if (id && !newSet.has(String(id))) {
        newSet.delete(String(id))
      }
    })

    newSet.forEach((id) => {
      if (!data.find((el) => String((el as { [id: string]: string | number }).id) === id)) {
        newSet.delete(id)
      }
    })

    setSelectedRows(newSet)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const value = {
    selectedRows,
    onSelectRows,
    onUnselectRows,
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export default SelectedRowsProvider
